import React, { useState, useMemo } from 'react';
import { X, Download, CheckCircle, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = '';

const COLORS = {
  vertForet: '#1e4a1a',
  vertForetBorder: '#2d5a27',
  orSable: '#c4a76c',
};

const isNameValid = (v) => v.length > 0 && /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/.test(v);
const isPhoneValid = (v) => {
  const digits = v.replace(/\D/g, '');
  return digits.length >= 10;
};

const BrochureModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [consent, setConsent] = useState(false);
  const [touched, setTouched] = useState({ name: false, phone: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const nameValid = isNameValid(formData.name);
  const phoneValid = isPhoneValid(formData.phone);

  const canSubmit = useMemo(
    () => nameValid && phoneValid && consent && !isSubmitting,
    [nameValid, phoneValid, consent, isSubmitting]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const getBorderStyle = (field) => {
    const value = formData[field];
    const isTouched = touched[field];
    if (!isTouched || value.length === 0) return { borderColor: '#d6d3d1' };
    const valid = field === 'name' ? isNameValid(value) : isPhoneValid(value);
    return { borderColor: valid ? COLORS.vertForetBorder : COLORS.orSable };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);

    try {
      await fetch(`${API_URL}/api/public/brochure-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, consent: true }),
      });
    } catch (err) { console.error('Lead submission failed:', err); }

    setIsSuccess(true);
    setIsSubmitting(false);

    const link = document.createElement('a');
    link.href = '/brochure-ogd.pdf';
    link.download = 'OneGreenDev-Brochure.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      onClose();
      setIsSuccess(false);
      setFormData({ name: '', phone: '' });
      setConsent(false);
      setTouched({ name: false, phone: false });
    }, 3000);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={handleBackdropClick}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="brochure-modal-overlay"
      >
        <motion.div
          className="relative w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          {/* Header */}
          <div className="px-8 pt-8 pb-6 rounded-t-2xl relative" style={{ backgroundColor: COLORS.vertForet }}>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              data-testid="brochure-modal-close"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
                <Download className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Notre Brochure
                </h3>
                <p className="text-white/60 text-xs tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  ONE GREEN DEV
                </p>
              </div>
            </div>
            <p className="text-white/70 text-sm leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Renseignez vos coordonnées pour recevoir notre brochure de présentation.
            </p>
          </div>

          {/* Form body */}
          <div className="bg-white px-8 py-6 rounded-b-2xl">
            {isSuccess ? (
              <motion.div
                className="text-center py-8"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: COLORS.vertForetBorder }} />
                <p className="font-semibold text-base" style={{ fontFamily: "'Montserrat', sans-serif", color: COLORS.vertForet }}>
                  Merci.
                </p>
                <p className="text-sm mt-1" style={{ fontFamily: "'Montserrat', sans-serif", color: '#78716c' }}>
                  Votre téléchargement commence...
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label htmlFor="brochure-name" className="block text-sm font-medium mb-1.5"
                    style={{ fontFamily: "'Montserrat', sans-serif", color: COLORS.vertForet }}
                  >
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="brochure-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={() => handleBlur('name')}
                    className="w-full px-4 py-3 rounded-lg border-2 outline-none transition-all text-sm bg-stone-50"
                    style={{ ...getBorderStyle('name'), fontFamily: "'Montserrat', sans-serif" }}
                    placeholder="Votre nom"
                    data-testid="brochure-modal-name"
                  />
                  {touched.name && formData.name.length > 0 && !nameValid && (
                    <p className="mt-1 text-xs" style={{ color: COLORS.orSable, fontFamily: "'Montserrat', sans-serif" }}
                      data-testid="brochure-name-error"
                    >
                      Format requis : Lettres uniquement
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="brochure-phone" className="block text-sm font-medium mb-1.5"
                    style={{ fontFamily: "'Montserrat', sans-serif", color: COLORS.vertForet }}
                  >
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    id="brochure-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={() => handleBlur('phone')}
                    className="w-full px-4 py-3 rounded-lg border-2 outline-none transition-all text-sm bg-stone-50"
                    style={{ ...getBorderStyle('phone'), fontFamily: "'Montserrat', sans-serif" }}
                    placeholder="+225 00 00 00 00"
                    data-testid="brochure-modal-phone"
                  />
                  {touched.phone && formData.phone.length > 0 && !phoneValid && (
                    <p className="mt-1 text-xs" style={{ color: COLORS.orSable, fontFamily: "'Montserrat', sans-serif" }}
                      data-testid="brochure-phone-error"
                    >
                      Numéro incomplet (10 chiffres minimum)
                    </p>
                  )}
                </div>

                {/* Consent checkbox */}
                <label className="flex items-start gap-3 cursor-pointer select-none group" data-testid="brochure-consent-label">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="sr-only peer"
                      data-testid="brochure-modal-consent"
                    />
                    <div
                      className="w-5 h-5 rounded border-2 transition-all flex items-center justify-center peer-checked:border-transparent"
                      style={{
                        borderColor: consent ? COLORS.orSable : '#d6d3d1',
                        backgroundColor: consent ? COLORS.orSable : 'transparent',
                      }}
                    >
                      {consent && (
                        <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 6l3 3 5-5" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-xs leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", color: COLORS.vertForet }}>
                    J'accepte d'être recontacté(e) par un conseiller One Green Dev pour mon projet immobilier.
                  </span>
                </label>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-white text-sm font-medium uppercase tracking-wider rounded-lg transition-all"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    backgroundColor: COLORS.vertForet,
                    opacity: canSubmit ? 1 : 0.5,
                    cursor: canSubmit ? 'pointer' : 'not-allowed',
                  }}
                  data-testid="brochure-modal-submit"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Valider & Télécharger
                    </>
                  )}
                </button>

                <p className="text-xs text-center leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", color: '#a8a29e' }}>
                  Vos données sont protégées conformément au RGPD.
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BrochureModal;
